import React, { useMemo } from 'react';
import Field from '../../Field';
import { READING_WORDING } from '../../../domain/reading';
import PhoneField from '../../PhoneField';
import { Radio } from '../../FormControls';
import { timeZoneOptions } from '../../../lib/intlOptions';
import { FAMILY_CONTACT_TEXT_MAX } from '../../../domain/people';
import SmsUnavailable, { smsOffClass } from '../../SmsUnavailable';
import { useSmsAvailable } from '../../../context/SmsGateContext';
import { smsShown } from '../../../domain/smsGate';
import s from './addStudent.module.css';

// Step 2: a student is always attached to a family, the billing entity, and
// the two are created together in one batch, so a student never exists with
// nobody to invoice.
//
// ── Time Zone is here, where MMS puts it ───────────────────────────────────
//
// With Multi-Time Zone Selection on, MMS's wizard offers Time Zone in its
// Family Details step, under Additional Details, as the last field before
// Parent Lesson Reminders; the student step has none. The sentence under it:
//
//     This time zone will also apply to all contacts and Students in this
//     student's Family.
//
// Two knowing differences. MMS has no "same as the studio" option — its
// dropdown is always a concrete zone, pre-filled with the studio's — where we
// keep null as a real value, because a stored copy of a setting goes stale
// when the setting changes. And we list IANA identifiers rather than MMS's
// Windows display names (`timeZoneOptions` in lib/intlOptions.js says why).

// ── An adult's new family asks for no name ─────────────────────────────────
//
// With Student Type Adult, MMS's Family Details keeps both radios and, for A
// new family, drops Parent First Name, Parent Last Name, Email and Mobile
// Phone: the account is made from the student, named after them, with them as
// its contact and invoice recipient. `asksForParent` is the name half of that
// rule, the core's (`core/src/add_student.rs`, whose header has where we
// knowingly differ).
//
// ── The new contact's address and Lesson Reminders (#792) ──────────────────
//
// A student is not a message destination: every message goes to the family's
// contacts, and a lesson reminder to the contacts who ticked its channel. So
// every new family — a child's or an adult's — is asked here for its contact's
// Email, Phone, SMS Capable and MMS's "Parent Lesson Reminders" ticks; for an
// adult they are the adult's own, which MMS asks on Student Details. An
// existing family gains no contact, so asks for none of them.

// One tick, drawn dimmed while the SMS gate holds it (#782).
function CheckRow({ checked, onChange, label, disabled = false }) {
  return (
    <label className={disabled ? `${s.checkRow} ${smsOffClass}` : s.checkRow}>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} />
      <span>{label}</span>
    </label>
  );
}

export default function FamilyStep({
  form, set, families, familiesRead = true, multiTimeZone = false, emailError = null, asksForParent = true,
  adult = false,
}) {
  // Whether a text can be sent at all (#782): the SMS tick is drawn off and
  // disabled while it cannot, as on the contact drawer.
  const smsAvailable = useSmsAvailable();
  // An unread list of families is not "No family accounts yet" (#629): the
  // existing-family choice waits, disabled and unexplained, for the answer.
  const noFamiliesYet = families.length === 0;
  const familiesUnread = noFamiliesYet && !familiesRead;
  // Four hundred-odd options, derived from Intl once and cached in the module,
  // memoised so the list is not rebuilt on every keystroke.
  const zones = useMemo(() => (multiTimeZone ? timeZoneOptions() : []), [multiTimeZone]);

  return (
    <>
      <div className={s.groupLabel}>Add this student to:</div>
      <Radio
        name="fam" label="A new family"
        help="Creates a new account in Families & Invoices"
        checked={form.familyChoice === 'new'}
        onChange={() => set('familyChoice', 'new')}
      />
      <Radio
        name="fam" label="An existing family"
        help={familiesUnread
          ? READING_WORDING.loading
          : noFamiliesYet
            ? READING_WORDING['addStudent.noFamilies']
            : 'Pick from an existing account in Families & Invoices'}
        disabled={noFamiliesYet}
        checked={form.familyChoice === 'existing'}
        onChange={() => set('familyChoice', 'existing')}
      />

      {form.familyChoice === 'existing' ? (
        <div className={[s.spaced, s.medium].join(' ')}>
          <Field label="Family">
            <select
              className={s.input}
              value={form.existingFamilyId}
              onChange={(e) => set('existingFamilyId', e.target.value)}
            >
              <option value="">Select…</option>
              {families.map((x) => <option key={x.id} value={x.id}>{x.label}</option>)}
            </select>
          </Field>
        </div>
      ) : (
        <>
          {asksForParent ? (
          <div className={s.pair}>
            {/* Bounded by the rule that stores them (#120): `validFamilyContact`
                (#319), because these go into a `familyContacts` record and the
                family keeps only its own name.

                `parentLastName` does double duty: the contact's surname and,
                where the student's own is not used instead, the family's
                `lastName`, which an invoice is addressed to and the families
                list sorts by. The two ceilings are the same 199, so nothing is
                truncated. */}
            <Field label="Parent First Name">
              <input
                className={s.input} maxLength={FAMILY_CONTACT_TEXT_MAX.firstName}
                value={form.parentFirstName}
                onChange={(e) => set('parentFirstName', e.target.value)}
              />
            </Field>
            <Field label="Parent Last Name">
              <input
                className={s.input} maxLength={FAMILY_CONTACT_TEXT_MAX.lastName}
                value={form.parentLastName}
                onChange={(e) => set('parentLastName', e.target.value)}
              />
            </Field>
          </div>
          ) : null}

          <div className={s.pair}>
            {/* Checked (#120): every invoice and reminder for this account is
                sent to this address, and `optionalEmail` in firestore.rules
                refuses a malformed one. */}
            <Field label="Email" hint="Optional" error={emailError}>
              <input
                type="email" className={s.input} maxLength={FAMILY_CONTACT_TEXT_MAX.email}
                value={form.parentEmail}
                onChange={(e) => set('parentEmail', e.target.value)}
              />
            </Field>
            {/* The tick qualifies this number: consent to text this contact,
                collected once, at intake. `address_on` in core/src/messages.rs
                reads it as part of the SMS address, so an unticked number is
                not an address. */}
            <div className={s.pairCol}>
              {/* Same field and validation (src/domain/phone.js, #90): an
                  invoice reminder is texted to this number. */}
              <PhoneField
                id="family-phone"
                label="Phone"
                value={form.parentPhone}
                onChange={(v) => set('parentPhone', v)}
              />
              <label className={s.checkRow}>
                <input
                  type="checkbox" checked={form.parentSms}
                  onChange={(e) => set('parentSms', e.target.checked)}
                />
                <span>SMS Capable</span>
              </label>
            </div>
          </div>

          {/* MMS asks the two reminder questions separately — "Send email
              lesson reminders" and "Send SMS lesson reminders", both off — and
              so do we: a family may want the email and not the text, and
              `contactWantsReminder` in domain/notifications.js reads a flag per
              channel. They are the contact's, as the contact drawer's are.

              Ticking SMS is not on its own enough to text anybody: the reminder
              also needs the number above marked SMS Capable. This box says the
              contact wants reminders; that tick says they consented to be
              texted at that number. */}
          <div className={s.groupLabelSpaced}>{adult ? 'Lesson Reminders' : 'Parent Lesson Reminders'}</div>
          <CheckRow
            label="Send email lesson reminders"
            checked={form.parentEmailReminders}
            onChange={(e) => set('parentEmailReminders', e.target.checked)}
          />
          {/* Disabled and drawn off while texts cannot be sent (#782,
              domain/smsGate.js); the form keeps whatever it holds. */}
          <CheckRow
            label="Send SMS lesson reminders"
            checked={smsShown(form.parentSmsReminders, smsAvailable)}
            onChange={(e) => { if (smsAvailable) set('parentSmsReminders', e.target.checked); }}
            disabled={!smsAvailable}
          />
          {smsAvailable ? null : <SmsUnavailable />}
          {/* MMS's own sentence on the contact drawer, as FamilyContactDrawer
              has it: the lead time lives on the event category and every
              category ships with reminders off (#87), so ticking either box is
              one of two gates. */}
          <div className={s.hint}>
            This family contact will only receive reminders for categories with
            reminders enabled. To manage them, go to Categories &amp; Locations.
          </div>

          {/* Only when the studio has turned on Multi-Time Zone Selection
              (Business Settings → General → Event Scheduling), as MMS gates it:
              with the box unticked there is no zone field on any of its forms.
              Most studios teach everybody in one zone.

              Nothing here moves a lesson: a lesson is anchored in the zone its
              series was created in, as in MMS. What this decides is what the
              reminder and the cancellation notice tell this family
              (src/domain/timeZone.js, #47). */}
          {multiTimeZone ? (
            <div className={[s.spaced, s.medium].join(' ')}>
              <Field label="Time Zone" hint="Applies to everyone on this family account">
                <select
                  className={s.input}
                  value={form.familyTimeZone}
                  onChange={(e) => set('familyTimeZone', e.target.value)}
                >
                  <option value="">Same as the studio</option>
                  {zones.map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
                </select>
              </Field>
            </div>
          ) : null}
        </>
      )}
    </>
  );
}
